import { useState, useEffect, useCallback } from 'react';

const API_KEY_STORAGE_KEY = 'car-ia-api-key';
const STORE_ID_STORAGE_KEY = 'car-ia-store-id';

export function useApiKey() {
  const [apiKey, setApiKeyState] = useState<string | null>(null);
  const [storeId, setStoreIdState] = useState<string | null>(null);
  const [storeName, setStoreNameState] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load from localStorage on init
  useEffect(() => {
    const loadStoredKey = () => {
      try {
        const storedKey = localStorage.getItem(API_KEY_STORAGE_KEY);
        const storedStoreId = localStorage.getItem(STORE_ID_STORAGE_KEY);
        const storedStoreName = localStorage.getItem(`${STORE_ID_STORAGE_KEY}-name`);
        
        if (storedKey && storedStoreId) {
          setApiKeyState(storedKey);
          setStoreIdState(storedStoreId);
          setStoreNameState(storedStoreName || 'Loja configurada');
          setIsConfigured(true);
        }
      } catch (error) {
        console.error('Failed to load API key from storage:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStoredKey();
  }, []);

  const configure = useCallback((key: string, storeId: string, storeName?: string) => {
    try {
      localStorage.setItem(API_KEY_STORAGE_KEY, key);
      localStorage.setItem(STORE_ID_STORAGE_KEY, storeId);
      if (storeName) {
        localStorage.setItem(`${STORE_ID_STORAGE_KEY}-name`, storeName);
      }
      
      setApiKeyState(key);
      setStoreIdState(storeId);
      setStoreNameState(storeName || 'Loja configurada');
      setIsConfigured(true);
    } catch (error) {
      console.error('Failed to save API key:', error);
      throw new Error('Falha ao salvar configuração');
    }
  }, []);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(API_KEY_STORAGE_KEY);
      localStorage.removeItem(STORE_ID_STORAGE_KEY);
      localStorage.removeItem(`${STORE_ID_STORAGE_KEY}-name`);
      
      setApiKeyState(null);
      setStoreIdState(null);
      setStoreNameState(null);
      setIsConfigured(false);
    } catch (error) {
      console.error('Failed to clear API key:', error);
    }
  }, []);

  const getAuthHeaders = useCallback(() => {
    if (!apiKey) return {};
    return { 'X-Api-Key': apiKey };
  }, [apiKey]);

  return {
    apiKey,
    storeId,
    storeName,
    isConfigured,
    loading,
    configure,
    clear,
    getAuthHeaders,
  };
}

// Hook for generating device ID (persisted)
const DEVICE_ID_KEY = 'car-ia-device-id';

export function useDeviceId(): string {
  const [deviceId, setDeviceId] = useState<string>('');

  useEffect(() => {
    let stored = localStorage.getItem(DEVICE_ID_KEY);
    if (!stored) {
      stored = crypto.randomUUID();
      localStorage.setItem(DEVICE_ID_KEY, stored);
    }
    setDeviceId(stored);
  }, []);

  return deviceId;
}